import * as Yup from 'yup';

import { CategoryPortal, Declaration } from '@src/app/interfaces/debtcon';
import { normalizeNumber } from '@src/shared/helpers';
import {
    isValidDateFormat,
    months,
    toApiDate,
    toValidDate,
    years,
} from '@src/shared/helpers/date';
import { CurrentDebt } from '@src/shared/helpers/debtCon';
import { securedLenderNames } from '@src/shared/lists/securedLenders';
import { unsecuredLenderNames } from '@src/shared/lists/unsecuredLenders';

import { requiredErrorMessage } from './applicantDetails';

export enum DebtLabelEnum {
    CreditCard = 'Credit card',
    ShoppingAccount = 'Shopping account',
    PersonalLoan = 'Personal loan',
    HirePurchase = 'Hire purchase',
    Overdraft = 'Overdraft',
    FixedTermCreditSale = 'Fixed-term credit sale',
    Lease = 'Lease',
    FixedTermDeferredPayment = 'Fixed-term deferred payment',
    PayDayLoan = 'Pay Day loan',
    RevolvingAccount = 'Revolving account',
    ChargeCard = 'Charge card',
    Account = 'Account',
    Mortgage = 'Mortgage',
    SecuredLoan = 'Secured loan',
    SecondMortgage = 'Second Mortgage',
    BuyToLetMortgage = 'Buy to Let Mortgage',
    ResidentialMortgage = 'Residential Mortgage',
}

export enum DebtTypeEnum {
    CreditCard = 'Credit card / Store card',
    MailOrder = 'Mail Order',
    UnsecuredLoan = 'Unsecured loan (personal loans etc)',
    HirePurchase = 'Hire purchase/Conditional sale',
    CurrentAccounts = 'Current accounts',
    CreditSaleFixedTerm = 'Credit sale fixed term',
    OperatingLease = 'Operating lease',
    FixedTermDeferredPayment = 'Fixed term deferred payment',
    PayDayLoans = 'Pay Day Loans',
    Bank = 'Bank',
    BudgetRevolvingAccount = 'Budget (revolving account)',
    ChargeCard = 'Charge card',
    Mortgage = 'Mortgage',
    SecondMortgage = 'Second mortgage (secured loan)',
    FlexibleMortgages = 'Flexible Mortgages',
    ResidentialMortgage = 'Residential Mortgage',
    BuyToLetMortgage = 'Buy to Let Mortgage',
}

const securedLenders = securedLenderNames.map((name) => ({
    label: name,
    value: name,
}));

const unsecuredLenders = unsecuredLenderNames.map((name) => ({
    label: name,
    value: name,
}));

export const unsecuredTypes = [
    DebtLabelEnum.Account,
    DebtLabelEnum.ChargeCard,
    DebtLabelEnum.CreditCard,
    DebtLabelEnum.FixedTermCreditSale,
    DebtLabelEnum.FixedTermDeferredPayment,
    DebtLabelEnum.HirePurchase,
    DebtLabelEnum.Lease,
    DebtLabelEnum.Overdraft,
    DebtLabelEnum.PayDayLoan,
    DebtLabelEnum.PersonalLoan,
    DebtLabelEnum.RevolvingAccount,
    DebtLabelEnum.ShoppingAccount,
];

// const securedTypes = [
//     DebtLabelEnum.Mortgage,
//     DebtLabelEnum.SecuredLoan,
//     DebtLabelEnum.SecondMortgage,
//     DebtLabelEnum.BuyToLetMortgage,
//     DebtLabelEnum.ResidentialMortgage,
// ];

const debtTypes = [...unsecuredTypes].map((label) => ({
    label,
    value: label,
}));

const labelToTypeMapping: Record<DebtLabelEnum, DebtTypeEnum> = {
    [DebtLabelEnum.CreditCard]: DebtTypeEnum.CreditCard,
    [DebtLabelEnum.FixedTermCreditSale]: DebtTypeEnum.CreditSaleFixedTerm,
    [DebtLabelEnum.HirePurchase]: DebtTypeEnum.HirePurchase,
    [DebtLabelEnum.Overdraft]: DebtTypeEnum.CurrentAccounts,
    [DebtLabelEnum.PersonalLoan]: DebtTypeEnum.UnsecuredLoan,
    [DebtLabelEnum.ShoppingAccount]: DebtTypeEnum.MailOrder,
    [DebtLabelEnum.Lease]: DebtTypeEnum.OperatingLease,
    [DebtLabelEnum.FixedTermDeferredPayment]:
        DebtTypeEnum.FixedTermDeferredPayment,
    [DebtLabelEnum.PayDayLoan]: DebtTypeEnum.PayDayLoans,
    [DebtLabelEnum.Account]: DebtTypeEnum.Bank,
    [DebtLabelEnum.RevolvingAccount]: DebtTypeEnum.BudgetRevolvingAccount,
    [DebtLabelEnum.ChargeCard]: DebtTypeEnum.ChargeCard,
    [DebtLabelEnum.Mortgage]: DebtTypeEnum.Mortgage,
    [DebtLabelEnum.SecondMortgage]: DebtTypeEnum.SecondMortgage,
    [DebtLabelEnum.BuyToLetMortgage]: DebtTypeEnum.BuyToLetMortgage,
    [DebtLabelEnum.SecuredLoan]: DebtTypeEnum.FlexibleMortgages,
    [DebtLabelEnum.ResidentialMortgage]: DebtTypeEnum.ResidentialMortgage,
};

const typeToLabelMapping: Record<DebtTypeEnum, DebtLabelEnum> = {
    [DebtTypeEnum.CreditCard]: DebtLabelEnum.CreditCard,
    [DebtTypeEnum.CreditSaleFixedTerm]: DebtLabelEnum.FixedTermCreditSale,
    [DebtTypeEnum.HirePurchase]: DebtLabelEnum.HirePurchase,
    [DebtTypeEnum.CurrentAccounts]: DebtLabelEnum.Overdraft,
    [DebtTypeEnum.UnsecuredLoan]: DebtLabelEnum.PersonalLoan,
    [DebtTypeEnum.MailOrder]: DebtLabelEnum.ShoppingAccount,
    [DebtTypeEnum.OperatingLease]: DebtLabelEnum.Lease,
    [DebtTypeEnum.FixedTermDeferredPayment]:
        DebtLabelEnum.FixedTermDeferredPayment,
    [DebtTypeEnum.PayDayLoans]: DebtLabelEnum.PayDayLoan,
    [DebtTypeEnum.BudgetRevolvingAccount]: DebtLabelEnum.RevolvingAccount,
    [DebtTypeEnum.ChargeCard]: DebtLabelEnum.ChargeCard,
    [DebtTypeEnum.Bank]: DebtLabelEnum.Account,
    [DebtTypeEnum.Mortgage]: DebtLabelEnum.Mortgage,
    [DebtTypeEnum.SecondMortgage]: DebtLabelEnum.SecondMortgage,
    [DebtTypeEnum.FlexibleMortgages]: DebtLabelEnum.SecuredLoan,
    [DebtTypeEnum.ResidentialMortgage]: DebtLabelEnum.ResidentialMortgage,
    [DebtTypeEnum.BuyToLetMortgage]: DebtLabelEnum.BuyToLetMortgage,
};

const requiredWhenNotPaidDebt = (schema: Yup.AnySchema, errorMsg?: string) => {
    return schema.when('ignore', {
        is: false,
        then: schema.required(errorMsg || requiredErrorMessage),
    });
};

const requiredWhenNotPaidDebtAndPromotionalPeriod = (
    schema: Yup.AnySchema,
    errorMsg: string,
) => {
    return schema.when(['ignore', 'interestFreePeriod'], {
        is: (ignore: boolean, interestFreePeriod: boolean) =>
            !ignore && interestFreePeriod,
        then: schema.required(errorMsg),
    });
};

const numericTransform = (target: string) => ({
    target,
    toRequest: (value: string) => normalizeNumber(value),
});

export const sharedFormItems = (category: CategoryPortal, type?: string) => ({
    month: {
        items: months,
    },
    year: {
        items: years,
    },
    startMonth: {
        validation: Yup.string().required('Please select month'),
    },
    startYear: {
        validation: Yup.string().required('Please select year'),
    },
    startDate: {
        validation: Yup.string()
            .test('validDate', 'It is not a valid date', (value) => {
                return isValidDateFormat(value + '');
            })
            .test(
                'notFuture',
                'Start date cannot be in the future',
                (value) => {
                    if (typeof value !== 'string') return true;

                    const [, mm, yyyy] = value.split('/');
                    const selected = new Date(Number(yyyy), Number(mm) - 1, 1);

                    const current = new Date();
                    current.setDate(1);

                    return selected <= current;
                },
            ),
        transform: {
            target: 'startDate',
            toRequest: (value: string) => toApiDate(value),
            fromRequest: (value: string) => toValidDate(value),
        },
    },
    endDate: {
        transform: {
            fromRequest: (value: string) => toValidDate(value),
        },
    },
    ignore: {
        default: false,
        validation: Yup.boolean().required(requiredErrorMessage),
        transform: {
            target: 'ignore',
            fromRequest: (value: string) => value || false,
        },
    },
    type: {
        items: debtTypes,
        validation: Yup.string().nullable().required(requiredErrorMessage),
        transform: {
            target: 'type',
            toRequest: (value: DebtLabelEnum) =>
                labelToTypeMapping[value] || value,
            fromRequest: (value: DebtTypeEnum) =>
                typeToLabelMapping[value] || value,
        },
    },
    lenderName: {
        items:
            category === CategoryPortal.MortgagesAndSecured
                ? securedLenders
                : unsecuredLenders,
        validation: requiredWhenNotPaidDebt(Yup.string().nullable()),
        transform: { target: 'lenderName' },
    },
    amountToConsolidate: {
        validation: requiredWhenNotPaidDebt(Yup.string().nullable()),
        transform: {
            ...numericTransform('amountToConsolidate'),
            fromRequest: (value: string, dependencies: any) => {
                return value || dependencies.outstandingBalance;
            },
        },
    },
    outstandingBalance: {
        transform: {
            target: 'outstandingBalance',
            toRequest: (value: string, dependencies: any) =>
                normalizeNumber(value || dependencies.amountToConsolidate),
        },
    },
    monthlyPaymentCustomerVerified: {
        validation: requiredWhenNotPaidDebt(
            Yup.string()
                .nullable()
                .test(
                    'monthlyPaymentCheck',
                    'Monthly payment cannot be greater than the balance',
                    function (value) {
                        if (!value) return true;
                        const amountToConsolidate = (this.parent as any)
                            ?.amountToConsolidate;
                        return (
                            normalizeNumber(value) <=
                            normalizeNumber(amountToConsolidate)
                        );
                    },
                ),
        ),
        transform: {
            ...numericTransform('monthlyPaymentCustomerVerified'),
            fromRequest: (value: string, dependencies: any) => {
                return value || dependencies.monthlyPayment;
            },
        },
    },
    monthlyPayment: {
        transform: {
            target: 'monthlyPayment',
            toRequest: (value: string, dependencies: any) =>
                normalizeNumber(
                    value || dependencies.monthlyPaymentCustomerVerified,
                ),
        },
    },
    interestRate: {
        validation: requiredWhenNotPaidDebt(
            Yup.string()
                .nullable()
                .test(
                    'maxValue',
                    type === DebtLabelEnum.CreditCard
                        ? 'Rate must be between 1 and 50'
                        : 'Rate must be 50 or less',
                    (value) =>
                        !value ||
                        (type === DebtLabelEnum.CreditCard
                            ? parseFloat(value) > 0 && parseFloat(value) <= 50
                            : parseFloat(value) <= 50),
                ),
        ),
        transform: numericTransform('interestRate'),
    },
});

export const creditCardOrMailOrderFormItems = (currentDebt: CurrentDebt) => ({
    interestFreePeriod: {
        default: null,
        validation: requiredWhenNotPaidDebt(Yup.boolean().nullable()),
        transform: { target: 'interestFreePeriod' },
    },
    endMonth: {
        validation: requiredWhenNotPaidDebtAndPromotionalPeriod(
            Yup.string().nullable(),
            'Please select month',
        ),
    },
    endYear: {
        validation: requiredWhenNotPaidDebtAndPromotionalPeriod(
            Yup.string().nullable(),
            'Please select year',
        ),
    },
    interestFreeEndDate: {
        validation: requiredWhenNotPaidDebtAndPromotionalPeriod(
            Yup.string().nullable(),
            'Please enter a valid date',
        ).test(
            'dateInPast',
            'The date cannot be in the past',
            (value, context) => {
                if (!isValidDateFormat(value)) return true;

                const { ignore, interestFreePeriod } = context.parent;
                if (!ignore && interestFreePeriod) {
                    const [_, month, year] = value.split('/');
                    const selectedDate = new Date(
                        Number(year),
                        Number(month) - 1,
                        1,
                    );

                    const currentDate = new Date();
                    currentDate.setDate(1);

                    return (
                        selectedDate.getFullYear() >
                            currentDate.getFullYear() ||
                        (selectedDate.getFullYear() ===
                            currentDate.getFullYear() &&
                            selectedDate.getMonth() >= currentDate.getMonth())
                    );
                }
                return true;
            },
        ),
        transform: {
            target: 'interestFreeEndDate',
            toRequest: (value: string) => toApiDate(value),
            fromRequest: (value: string) => toValidDate(value),
        },
    },
    ...sharedFormItems(currentDebt.categoryPortal, currentDebt.type),
});

export const securedFormItems = (category: CategoryPortal) => ({
    securityProperty: {
        default: null,
        validation: requiredWhenNotPaidDebt(Yup.boolean().nullable()),
        transform: { target: 'securityProperty' },
    },
    ...sharedFormItems(category),
});

export const newDebtFormItems = (
    applicants: any,
    currentDebt: CurrentDebt,
    creditCardOrMailOrder: boolean,
) => ({
    declaration: {
        default: Declaration.User,
    },
    owner: {
        default: null,
        items: [
            { label: 'Joint', value: 'Joint' },
            {
                label: `${applicants[0].firstName} ${applicants[0].lastName}`,
                value: `${applicants[0].firstName} ${applicants[0].lastName}`,
            },
        ],
        validation: Yup.string()
            .nullable()
            .test('selectOwner', requiredErrorMessage, (value) => {
                if (applicants.length === 2) {
                    return !!value;
                }
                return true;
            }),
        transform: {
            target: 'owner',
            fromRequest: (_: any, dependencies: any) =>
                dependencies.joint
                    ? 'Joint'
                    : `${applicants[0].firstName} ${applicants[0].lastName}`,
        },
    },
    joint: {
        transform: {
            target: 'joint',
            toRequest: (_: any, dependencies: any) =>
                dependencies.owner === 'Joint' ? true : false,
        },
    },
    primaryApplicant: {
        transform: {
            target: 'primaryApplicant',
            toRequest: () => true, // primaryApplicant is always true for user defined debts
        },
    },
    categoryPortal: {
        transform: {
            target: 'categoryPortal',
            toRequest: (_: any, dependencies: any) => {
                return unsecuredTypes.includes(dependencies.type)
                    ? CategoryPortal.Unsecured
                    : CategoryPortal.MortgagesAndSecured;
            },
        },
    },
    ...(creditCardOrMailOrder
        ? creditCardOrMailOrderFormItems(currentDebt)
        : sharedFormItems(
              currentDebt?.categoryPortal ?? CategoryPortal.Unsecured,
          )),
});
